import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape
} from 'react-intl';
import { DateTime } from 'luxon';
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Container,
  Alert
} from 'reactstrap';
import { withRouter } from 'react-router';
import RouterPropTypes from 'react-router-prop-types';
import { isEqual } from 'lodash';

import RouteService from '../../../services/RouteService';
import HelperNavigation from '../../common/HelperNavigation';
import globalTranslations from '../../../i18n/globalTranslations';
import Paths from '../../../types/Paths';
import { modelOf } from '../../../prop-types';
import OrderStore from '../../../store/OrderStore';
import ContentForState from '../../loader/ContentForState';
import SEOTitle from '../../head/SEOTitle';
import PageTitle from '../../common/PageTitle';
import CountryStore from '../../../store/CountryStore';
import ProductImage from '../../product/ProductImage';
import ProductImageModel from '../../../models/ProductImage';
import UIStore from '../../../store/UIStore';
import Icon from '../../common/Icon';
import OrderStatusLabel from '../../order/OrderStatusLabel';
import OrderTotalClass from '../../../types/OrderTotalClass';
import Price from '../../product/Price';
import ProductWarranty from '../../product/ProductWarranty';
import UnexpectedError from '../../loader/UnexpectedError';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import OrderCloneButton from '../../order/OrderCloneButton';
import ConfigStore from '../../../store/ConfigStore';
import ProductTypeClass from '../../../types/ProductTypeClass';
import AccountStore from '../../../store/AccountStore';

const messages = defineMessages({
  'myAccount.orderTitle': {
    id: 'myAccount.orderTitle',
    defaultMessage: 'Order {orderId}'
  }
});

const productTotals = [OrderTotalClass.TOTAL];

const billingInfoTotals = [
  OrderTotalClass.TAXLESS_TOTAL,
  OrderTotalClass.TAX_TOTAL,
  OrderTotalClass.TOTAL
];

const OrderHeaders = {
  image: <FormattedMessage {...globalTranslations.productTitle} />,
  name: <FormattedMessage id="order.productNameTitle" defaultMessage="Name" />,
  quantity: <FormattedMessage {...globalTranslations.quantityTitle} />,
  deliveredQuantity: (
    <FormattedMessage
      id="order.deliveredQuantityTitle"
      defaultMessage="Delivered qty"
    />
  ),
  unitPrice: (
    <FormattedMessage id="global.unitPriceTitle" defaultMessage="Unit price" />
  ),
  totalSum: (
    <FormattedMessage id="order.totalShortTitle" defaultMessage="Total" />
  )
};

const StatusHeaders = {
  date: <FormattedMessage id="order.dateTitle" defaultMessage="Date" />,
  status: <FormattedMessage {...globalTranslations.statusTitle} />,
  description: (
    <FormattedMessage
      id="order.statusDescriptionTitle"
      defaultMessage="Description"
    />
  )
};

const QueryStatus = {
  NONE: 'NONE',
  LOADING: 'LOADING',
  LOADED: 'LOADED',
  NOT_FOUND: 'NOT_FOUND',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR'
};

@observer
export class MyAccountOrder extends Component {
  state = {
    queryStatus: QueryStatus.NONE
  };

  componentDidMount() {
    this.loadOrder();
  }

  getCurrentOrderId = () => {
    return this.props.match.params.id;
  };

  loadOrder = () => {
    const { orderStore, code } = this.props;

    this.setState({
      queryStatus: QueryStatus.LOADING
    });

    orderStore
      .loadOrder(this.getCurrentOrderId(), code)
      .then(() => {
        this.setState({
          queryStatus: QueryStatus.LOADED
        });
      })
      .catch((e) => {
        const status = e.response ? e.response.status : 500;
        const errorMap = {
          404: QueryStatus.NOT_FOUND,
          500: QueryStatus.UNEXPECTED_ERROR
        };

        this.setState({
          queryStatus: errorMap[status]
        });
      });
  };

  getOrderState = () => {
    return this.state.queryStatus;
  };

  getOrder = () => {
    const { orderStore } = this.props;

    return orderStore.orders.get(this.getCurrentOrderId());
  };

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount
      },
      {
        text: intl.formatMessage(globalTranslations.myOrderHistoryTitle),
        url: Paths.MyAccountOrderList
      },
      {
        text: intl.formatMessage(messages['myAccount.orderTitle'], {
          orderId: this.getCurrentOrderId()
        }),
        url: Paths.MyAccountOrderList
      }
    ]);
  };

  createProductImage = (url, id) => {
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: id,
          sizes: {
            small: url
          }
        })
      : null;
  };

  renderPaymentAddressInfo = (address) => {
    return (
      <Fragment>
        <h5 className="MyAccountOrder__detail-header">
          <FormattedMessage
            id="myAccount.billingAddressTitle"
            defaultMessage="Billing address"
          />
        </h5>
        {this.renderAddressInfo(address)}
      </Fragment>
    );
  };

  renderAddressInfo = (address) => {
    const { countryStore } = this.props;

    return (
      <ul className="MyAccountOrder__address-info">
        <li>{address.name}</li>
        {address.company && <li>{address.company}</li>}
        {address.care_of && <li>{address.care_of}</li>}
        <li>{address.street_address}</li>
        {address.street_address2 && <li>{address.street_address2}</li>}
        <li>
          {address.postalcode} {address.city}
        </li>
        {address.country_id && (
          <li>{countryStore.findCountry(address.country_id).name}</li>
        )}
      </ul>
    );
  };

  renderProductHeaderCol = (label, sizes) => {
    const actualSizes = Object.assign(
      {
        xs: 4
      },
      sizes
    );
    return (
      <Col className="MyAccountOrder__product-header" {...actualSizes}>
        {label}
      </Col>
    );
  };

  renderProductHeaderRow = () => {
    return (
      <Row className="MyAccountOrder__product-headers">
        {this.renderProductHeaderCol(OrderHeaders.image, { md: 2, xl: 1 })}
        {this.renderProductHeaderCol(OrderHeaders.name, { md: 6, xl: 7 })}
        {this.renderProductHeaderCol(OrderHeaders.quantity, { md: 1 })}
        {this.renderProductHeaderCol(OrderHeaders.deliveredQuantity, { md: 1 })}
        {this.renderProductHeaderCol(OrderHeaders.unitPrice, { md: 1 })}
        {this.renderProductHeaderCol(OrderHeaders.totalSum, { md: 1 })}
      </Row>
    );
  };

  renderStatusHeaderCol = (label, sizes) => {
    return (
      <Col className="MyAccountOrder__status-header" {...sizes}>
        {label}
      </Col>
    );
  };

  renderStatusHeaderRow = () => {
    return (
      <Row className="MyAccountOrder__status-headers">
        {this.renderStatusHeaderCol(StatusHeaders.date, { md: 3, xl: 2 })}
        {this.renderStatusHeaderCol(StatusHeaders.status, { md: 4, xl: 3 })}
        {this.renderStatusHeaderCol(StatusHeaders.description, { md: 5 })}
      </Row>
    );
  };

  renderProductLink = (product) => {
    return product.isPhysicalProduct && product.product_url ? (
      <MaybeExternalLink to={product.product_url}>
        {product.name}
      </MaybeExternalLink>
    ) : (
      product.name
    );
  };

  renderProductCode = (product) => {
    const { configStore, accountStore } = this.props;
    if (configStore.productCard.showProductCode || accountStore.isRetailer) {
      const collectionOrMultiProduct = product.id.includes('{');
      const index = collectionOrMultiProduct
        ? product.id.indexOf('{')
        : product.id;
      const productCode = product.id.substring(0, index);
      return productCode;
    }
  };

  renderPaymentLogo = () => {
    const order = this.getOrder();
    if (order.payment.method && order.payment.method.logo) {
      return (
        <img
          src={order.payment.method.logo}
          alt={order.payment.method.name}
          loading="lazy"
        />
      );
    }
    return null;
  };

  renderShippingLogo = () => {
    const order = this.getOrder();
    if (order.shipping.method && order.shipping.method.logo) {
      return (
        <img
          src={order.shipping.method.logo}
          alt={order.shipping.method.name}
          loading="lazy"
        />
      );
    }
    return null;
  };

  renderProductRow = (product) => {
    const { isMobile } = this.props.uiStore;

    return (
      <Row key={product.id} className="MyAccountOrder__product-row">
        {!isMobile && (
          <Col className="MyAccountOrder__products-image" xs="2" xl="1">
            {product.isPhysicalProduct && (
              <MaybeExternalLink to={product.product_url}>
                <ProductImage
                  product={product}
                  productImage={this.createProductImage(
                    product.image_url,
                    product.id
                  )}
                  size="small"
                  forceAspectRatio={false}
                />
              </MaybeExternalLink>
            )}
          </Col>
        )}
        {isMobile && this.renderProductHeaderCol(OrderHeaders.name)}
        <Col xs="8" md="6" xl="7">
          <div className={'MyAccountOrder__products-name-wrapper'}>
            {this.renderProductLink(product)}
            {product &&
              product.type !== ProductTypeClass.SHIPPING_COST &&
              this.renderProductCode(product)}
          </div>
          {product.variations &&
            product.variations.length > 0 && (
              <ul>
                {product.variations.map((variation, index) => (
                  <li key={index}>{variation}</li>
                ))}
              </ul>
            )}
          {product.reference && (
            <div className="MyAccountOrder__products-reference">
              <FormattedMessage {...globalTranslations.yourReferenceTitle} />
              {': ' + product.reference}
            </div>
          )}
          {product.warranty_in_months > 0 && (
            <div className="MyAccountOrder__products-warranty">
              <ProductWarranty warranty={product.warranty_in_months} />
            </div>
          )}
        </Col>
        {product.isPhysicalProduct ? (
          <Fragment>
            {isMobile && this.renderProductHeaderCol(OrderHeaders.quantity)}
            <Col xs="8" md="1">
              {product.quantity_ordered} {product.stock_unit}
            </Col>
            {isMobile &&
              this.renderProductHeaderCol(OrderHeaders.deliveredQuantity)}
            <Col xs="8" md="1">
              {product.quantity_delivered} {product.stock_unit}
            </Col>
            {isMobile && this.renderProductHeaderCol(OrderHeaders.unitPrice)}
            <Col
              xs="8"
              md="1"
              dangerouslySetInnerHTML={{ __html: product.final_unit_price }}
            />
          </Fragment>
        ) : !isMobile ? (
          <Col md="3" />
        ) : null}
        {isMobile && this.renderProductHeaderCol(OrderHeaders.totalSum)}
        <Col
          xs="8"
          md="1"
          dangerouslySetInnerHTML={{ __html: product.total_price }}
        />
      </Row>
    );
  };

  renderTotals = (visibleTotalClasses) => {
    const order = this.getOrder();
    return (
      <div className="MyAccountOrder__totals">
        {order.totals
          .filter((total) => {
            return visibleTotalClasses.includes(total.class);
          })
          .map((totalsItem) => (
            <Row key={totalsItem.name}>
              <Col xs="8" className="MyAccountOrder__total-item">
                {totalsItem.name}
              </Col>
              <Col xs="4" className="MyAccountOrder__total-value">
                <Price price={totalsItem.value} />
              </Col>
            </Row>
          ))}
      </div>
    );
  };

  renderInfoComponent = (title, content) => {
    return (
      <Fragment>
        <h5 className="MyAccountOrder__detail-header">{title}</h5>
        <p>{content}</p>
      </Fragment>
    );
  };

  renderPickUpPoint = (pickUpPoint) => {
    return (
      <div className="MyAccountOrder__detail-pickup">
        <div className="MyAccountOrder__detail-pickup-name">
          {pickUpPoint.name}
        </div>
        <div className="MyAccountOrder__detail-pickup-address">
          {pickUpPoint.address}
        </div>
        <div className="MyAccountOrder__detail-pickup-contact-info">
          {pickUpPoint.contact_info}
        </div>
      </div>
    );
  };

  renderTracking = (tracking) => {
    return (
      <Fragment>
        <h5 className="MyAccountOrder__detail-header">
          <FormattedMessage
            id="myAccount.trackingTitle"
            defaultMessage="Tracking order parcels"
          />
        </h5>
        {tracking.map((code, index) => {
          return (
            <p key={code.code}>
              <FormattedMessage
                id="myAccount.packageTitle"
                defaultMessage="Packet {parcelCount}: "
                values={{
                  parcelCount: index + 1
                }}
              />{' '}
              <a href={code.url} target="_blank" rel="noopener">
                <FormattedMessage
                  id="myAccount.packageToTracking"
                  defaultMessage="{trackingCode} (Go to tracking)"
                  values={{
                    trackingCode: code.code
                  }}
                />{' '}
                <Icon name="angle-double-right" />
              </a>
            </p>
          );
        })}
        <FormattedMessage
          id="myAccount.trackingDescription"
          defaultMessage="(If the tracking page notifies you that no orders were found, it may be that the parcel is not yet received at the delivery company)"
        />
      </Fragment>
    );
  };

  shouldRenderPaymentAddress = () => {
    const order = this.getOrder();

    return !isEqual(order.shipping.address, order.payment.address);
  };

  renderMessage = () => {
    const { queryStatus } = this.state;
    let message = null;

    switch (queryStatus) {
      case QueryStatus.NOT_FOUND:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="order.orderNotFound"
              defaultMessage="Given code is not found"
            />
          </Alert>
        );
        break;
      case QueryStatus.UNEXPECTED_ERROR:
        message = <UnexpectedError />;
        break;
      case QueryStatus.NONE:
      case QueryStatus.LOADING:
      case QueryStatus.LOADED:
      default:
        break;
    }

    return message;
  };

  render() {
    const { uiStore, allowClone } = this.props;
    const { queryStatus } = this.state;
    const orderId = this.getCurrentOrderId();

    return (
      <Fragment>
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <SEOTitle
          title={{
            ...messages['myAccount.orderTitle'],
            values: { orderId }
          }}
        />
        {queryStatus && this.renderMessage()}
        <ContentForState
          state={this.getOrderState()}
          forLoaded={() => {
            const order = this.getOrder();
            return (
              <div className="MyAccountOrder">
                <PageTitle className="MyAccountOrder__page-title">
                  <FormattedMessage
                    {...messages['myAccount.orderTitle']}
                    values={{ orderId }}
                  />{' '}
                  <span className="MyAccountOrder__status">
                    <OrderStatusLabel status={order.last_status} />
                  </span>
                </PageTitle>
                <Container className="MyAccountOrder__container">
                  <Row>
                    <Col xl="6" md="12">
                      <Card>
                        <CardHeader>
                          <FormattedMessage
                            id="myAccount.shippingDetailsTitle"
                            defaultMessage="Shipping details"
                          />
                          <FormattedMessage
                            id="myAccount.orderDate"
                            defaultMessage="Ordered on: {orderDate}"
                            values={{
                              orderDate: DateTime.fromISO(
                                order.date_purchased
                              ).toLocaleString()
                            }}
                          />
                        </CardHeader>
                        <CardBody>
                          <Container className="MyAccountOrder__shipping-details-container">
                            <Row>
                              <Col>
                                <h5 className="MyAccountOrder__detail-header">
                                  <FormattedMessage
                                    id="myAccount.shippingAddressTitle"
                                    defaultMessage="Shipping address"
                                  />
                                </h5>
                                {this.renderAddressInfo(order.shipping.address)}
                                {order.seller_name &&
                                  this.renderInfoComponent(
                                    <FormattedMessage
                                      {...globalTranslations.retailerTitle}
                                    />,
                                    order.seller_name
                                  )}
                                {order.your_reference &&
                                  this.renderInfoComponent(
                                    <FormattedMessage
                                      {...globalTranslations.yourReferenceTitle}
                                    />,
                                    order.your_reference
                                  )}
                              </Col>
                              <Col>
                                <h5 className="MyAccountOrder__detail-header">
                                  <FormattedMessage
                                    id="myAccount.shippingMethodTitle"
                                    defaultMessage="Shipping method"
                                  />
                                </h5>
                                <div className="MyAccountOrder__method">
                                  <div className="MyAccountOrder__method-logo">
                                    {this.renderShippingLogo()}
                                  </div>
                                  <div className="MyAccountOrder__method-name">
                                    {order.shipping.method.name}
                                  </div>
                                </div>
                                {order.shipping.pick_up_point &&
                                  this.renderPickUpPoint(
                                    order.shipping.pick_up_point
                                  )}
                                {order.weight &&
                                  this.renderInfoComponent(
                                    <FormattedMessage
                                      {...globalTranslations.weightTitle}
                                    />,
                                    order.weight
                                  )}
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                {order.shipping.tracking.length > 0 &&
                                  this.renderTracking(order.shipping.tracking)}
                              </Col>
                            </Row>
                          </Container>
                        </CardBody>
                      </Card>
                    </Col>
                    <Col xl="6" md="12">
                      <Card>
                        <CardHeader>
                          <FormattedMessage
                            id="myAccount.paymentDetailsTitle"
                            defaultMessage="Payment details"
                          />
                          {order.receipt_url && (
                            <Button
                              tag="a"
                              href={order.receipt_url}
                              color="primary"
                              className="MyAccountOrder__payment-receipt-button"
                              target="_blank"
                            >
                              <Icon name="print" />{' '}
                              <FormattedMessage
                                id="myAccount.printReceiptSentence"
                                defaultMessage="Print a receipt of this order"
                              />
                            </Button>
                          )}
                        </CardHeader>
                        <CardBody>
                          <Container className="MyAccountOrder__payment-details-container">
                            <Row>
                              <Col xs="12" md="6">
                                {this.shouldRenderPaymentAddress() &&
                                  this.renderPaymentAddressInfo(
                                    order.payment.address
                                  )}

                                <h5 className="MyAccountOrder__detail-header">
                                  <FormattedMessage
                                    id="myAccount.billingMethodTitle"
                                    defaultMessage="Billing method"
                                  />
                                </h5>
                                <div className="MyAccountOrder__method">
                                  <div className="MyAccountOrder__method-logo">
                                    {this.renderPaymentLogo()}
                                  </div>
                                  <div className="MyAccountOrder__method-name">
                                    {order.payment.method.name}
                                  </div>
                                </div>
                              </Col>
                              <Col xs="12" md="6">
                                {this.renderTotals(billingInfoTotals)}
                              </Col>
                            </Row>
                            {allowClone && (
                              <Row>
                                <Col className="MyAccountOrder__clone-button">
                                  <OrderCloneButton order={this.getOrder()} />
                                </Col>
                              </Row>
                            )}
                          </Container>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Card>
                        <CardHeader>
                          <FormattedMessage
                            {...globalTranslations.productsTitle}
                          />
                        </CardHeader>
                        <CardBody>
                          <Container className="MyAccountOrder__products-container">
                            <div className="MyAccountOrder__products">
                              {!uiStore.isMobile &&
                                this.renderProductHeaderRow()}
                              {order.products.map((product) =>
                                this.renderProductRow(product)
                              )}
                            </div>
                            <Row className="MyAccountOrder__products-totals-row">
                              <Col md="9" />
                              <Col>{this.renderTotals(productTotals)}</Col>
                            </Row>
                          </Container>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Card>
                        <CardHeader>
                          <FormattedMessage
                            id="myAccount.orderHistoryTitle"
                            defaultMessage="Shipping history"
                          />
                        </CardHeader>
                        <CardBody>
                          {!uiStore.isMobile && this.renderStatusHeaderRow()}
                          {order.status_history.map((entry, index) => (
                            <Row
                              key={`${entry.date}_${entry.status}_${index}`}
                              className="MyAccountOrder__status-history-entry"
                            >
                              <Col md="3" xl="2">
                                {DateTime.fromISO(entry.date).toLocaleString()}
                              </Col>
                              <Col md="4" xl="3">
                                <OrderStatusLabel status={entry.status} />
                              </Col>
                              <Col md="5">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: entry.message
                                  }}
                                />
                              </Col>
                            </Row>
                          ))}
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </Container>
              </div>
            );
          }}
        />
      </Fragment>
    );
  }
}

MyAccountOrder.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  orderStore: modelOf(OrderStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  allowClone: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  code: PropTypes.string
};

export default injectIntl(
  withRouter(
    inject(
      'accountStore',
      'configStore',
      'countryStore',
      'orderStore',
      'routeService',
      'uiStore'
    )(MyAccountOrder)
  )
);



// WEBPACK FOOTER //
// ./src/components/my-account/MyAccountOrder/index.jsx